/**
 * Workshop Scheduler Utility
 * Automatically calculates and updates workshop timing for DAILY FREE sessions at 4:00 PM IST
 */

// Workshop configuration - Updated for DAILY 4:00 PM FREE sessions
const WORKSHOP_CONFIG = {
  // Daily sessions (no specific day restriction)
  startTime: { hour: 16, minute: 0 }, // 4:00 PM (16:00)
  endTime: { hour: 19, minute: 0 },   // 7:00 PM (19:00)
  timezone: 'Asia/Kolkata', // IST timezone
  duration: 3, // 3 hours
  type: 'free_webinar' // Updated to reflect free webinar
};

/**
 * Get the next workshop date from current date
 * @returns {Date} Next workshop date
 */
export function getNextWorkshopDate() {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Calculate next workshop time
  const workshopTime = WORKSHOP_CONFIG.startTime.hour * 60 + WORKSHOP_CONFIG.startTime.minute;
  const currentTime = currentHour * 60 + currentMinute;

  let nextWorkshop = new Date(now);

  if (currentTime < workshopTime) {
    // Workshop hasn't started yet today - use today
    nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
    nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  } else {
    // Workshop has passed today - use tomorrow
    nextWorkshop.setDate(now.getDate() + 1);
    nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
    nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  }

  nextWorkshop.setSeconds(0);
  nextWorkshop.setMilliseconds(0);

  return nextWorkshop;
}

/**
 * Get ordinal suffix for day (1st, 2nd, 3rd, 4th, etc.)
 * @param {number} day - Day of month
 * @returns {string} Ordinal suffix
 */
function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Get formatted workshop date string in exact format: "Wednesday, December 22nd"
 * @returns {string} Formatted date string
 */
export function getFormattedWorkshopDate() {
  const nextWorkshop = getNextWorkshopDate();
  
  const weekday = nextWorkshop.toLocaleDateString('en-US', { weekday: 'long', timeZone: WORKSHOP_CONFIG.timezone });
  const month = nextWorkshop.toLocaleDateString('en-US', { month: 'long', timeZone: WORKSHOP_CONFIG.timezone });
  const day = nextWorkshop.getDate();
  const ordinalSuffix = getOrdinalSuffix(day);
  
  return `${weekday}, ${month} ${day}${ordinalSuffix}`;
}

/**
 * Get workshop time string in format: "04:00 PM IST"
 * @returns {string} Workshop time string
 */
export function getWorkshopTimeString() {
  const startHour = WORKSHOP_CONFIG.startTime.hour;
  const startMinute = WORKSHOP_CONFIG.startTime.minute;
  
  // Convert 24-hour to 12-hour format
  const hour12 = startHour % 12 || 12;
  const ampm = startHour >= 12 ? 'PM' : 'AM';
  const formattedTime = `${hour12.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')} ${ampm} IST`;
  
  return formattedTime;
}

/**
 * Get countdown target date (next workshop start time)
 * @returns {Date} Target date for countdown
 */
export function getCountdownTargetDate() {
  return getNextWorkshopDate();
}

/**
 * Check if it's time to update (daily at 12:00 AM)
 * @returns {boolean} True if it's update time
 */
export function isUpdateTime() {
  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();
  return hour === 0 && minute === 0; // Daily 12:00 AM
}

/**
 * Get workshop status text
 * @returns {string} Status text
 */
export function getWorkshopStatus() {
  const now = new Date();
  const nextWorkshop = getNextWorkshopDate();
  const timeDiff = nextWorkshop.getTime() - now.getTime();
  const hoursLeft = Math.ceil(timeDiff / (1000 * 60 * 60));

  if (hoursLeft <= 1) {
    return "FREE Webinar starting soon!";
  } else if (hoursLeft <= 6) {
    return `FREE Webinar in ${hoursLeft} hours`;
  } else if (hoursLeft <= 24) {
    const formattedDate = getFormattedWorkshopDate();
    const today = new Date();
    const workshopDate = new Date(nextWorkshop);
    
    today.setHours(0, 0, 0, 0);
    workshopDate.setHours(0, 0, 0, 0);
    
    if (workshopDate.getTime() === today.getTime()) {
      return "FREE Webinar is TODAY!";
    } else {
      return `FREE Webinar ${formattedDate}`;
    }
  } else {
    return "Next FREE webinar coming soon";
  }
}

/**
 * Get workshop details object
 * @returns {Object} Workshop details
 */
export function getWorkshopDetails() {
  const nextWorkshop = getNextWorkshopDate();
  const formattedDate = getFormattedWorkshopDate();
  const timeString = getWorkshopTimeString();
  
  return {
    date: nextWorkshop,
    formattedDate: formattedDate,
    timeString: timeString,
    // Complete batch string in exact format: "Wednesday, December 22nd at 04:00 PM IST"
    batchString: `${formattedDate} at ${timeString}`,
    status: getWorkshopStatus(),
    countdown: nextWorkshop.toISOString(),
    dayOfWeek: 'Daily',
    duration: `${WORKSHOP_CONFIG.duration} hours`,
    timezone: 'IST',
    type: WORKSHOP_CONFIG.type,
    price: 'FREE'
  };
}